import { useQuery, useQueries, useMutation } from '@tanstack/react-query'
import { StatementApi as StatementApiClass } from '@pimy-b2cweb/apiclient-customer-stmt'
import { BaseApiConfig, apiClient } from './api-config'

import { store } from '@/stores'
import { selectAuthSessionState } from '@/stores/auth.selectors'
import { StatusEnum } from '@/config'

export const StmtApi = new StatementApiClass(BaseApiConfig, apiClient)
const source = 'B2C'

export const useQueryStmtRecent = (
  { cifId }: { cifId: string },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-recent', token, status, cifId],
    queryFn: async () => {
      const {
        data: {
          body: { statements, accountNumbers, accountsList },
        },
      } = await StmtApi.recent({
        stmtRecentReqBodyPayload: {
          accountNumbers: [],
          cifId,
        },
      })
      return { statements, accountNumbers, accountsList }
    },
    enabled: enabled && !!token && status === StatusEnum.LoggedIn,
  })
}

export const useQueryStmtSoaDocMsCifIdAccNoApi = (
  { country, idType, idNo }: { country: string; idType: string; idNo: number },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}

  return useQuery({
    queryKey: ['query-stmt-soa-docms-cifid-accno-api', idNo],
    queryFn: async () => {
      const response = await StmtApi.docMsCifIdAccNoApi({
        country,
        idType,
        idNo,
      })
      const docMsCifIdAccNoApiResponse = response.data.body
      return docMsCifIdAccNoApiResponse
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtSoaDocMsSearchApi = (
  {
    category,
    accountNos,
    limit = 10,
    page = 1,
  }: {
    category: number
    accountNos: string[] | undefined
    limit?: number
    page?: number
  },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}

  const queries = useQueries({
    queries: (accountNos ?? []).map((accountNo) => ({
      queryKey: [
        'query-stmt-soa-docms-search-api',
        category,
        accountNo,
        page,
        limit,
      ],
      keepPreviousData: true,
      queryFn: async () => {
        const response = await StmtApi.docMsSearchApi({
          stmtDocMsSearchReqBodyPayload: {
            category,
            account_no: accountNo,
            limit,
            page,
          },
        })
        const docMsSearchApiResponse = response?.data?.body
        return docMsSearchApiResponse
      },
      enabled:
        !!token && status === StatusEnum.LoggedIn && enabled && !!accountNo,
    })),
  })

  const isLoading = queries.some((q) => q.status === 'loading')
  const isError = queries.some((q) => q.status === 'error')

  const mergedBody = {
    list: queries.flatMap((q) => q.data?.list ?? []),
    count: queries.reduce((sum, q) => sum + (q.data?.count ?? 0), 0),
    page,
    limit,
  }

  return {
    data: mergedBody,
    status: isLoading ? 'loading' : isError ? 'error' : 'success',
    error: queries.find((q) => q.error)?.error,
    queries,
  }
}

export const useMutateStmtSoaDocMsDownloadApi = () => {
  return useMutation({
    mutationFn: async (docId: string) => {
      const response = await StmtApi.queryDocMsDownloadApiByDocId({ docId })
      return response.data.body
    },
  })
}

export const useMultipleContributionQueries = (
  accounts: { accountNo: string; idNo: string; prsIndicator: string }[],
  cifId: string,
  enabled: boolean
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQueries({
    queries: accounts.map(({ accountNo, idNo, prsIndicator }) => ({
      queryKey: [
        'query-stmt-contribution-summary',
        accountNo,
        idNo,
        prsIndicator,
      ],
      queryFn: async () => {
        const response = await StmtApi.queryContributionSummary({
          source,
          stmtContributionSummaryReqBodyPayload: {
            accountNo,
            idNo,
            prsIndicator,
            cifId,
          },
        })
        const investmentApproach = response.data.body?.investmentApproach
        const accountHoldings = response.data.body?.accountHoldings
        const isGHRPHR = response.data.body?.isGHRPHR
        return { accountHoldings, investmentApproach, isGHRPHR }
      },
      enabled: !!token && status === StatusEnum.LoggedIn && enabled,
    })),
  })
}

export const useQueryStmtAccountDetails = (
  {
    accountNo,
    idNo,
    isGHRPHR,
  }: { accountNo: string; idNo: string; isGHRPHR?: boolean },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-account-details', accountNo, idNo, isGHRPHR],
    queryFn: async () => {
      const response = await StmtApi.queryAccountDetails({
        source,
        stmtAccountDetailsReqBodyPayload: { accountNo, idNo, isGHRPHR },
      })
      const accountHoldings = response.data.body?.accountHoldings
      const AccountYtdContributonList =
        response.data.body?.AccountYtdContributonList
      const navDetails = response.data.body?.navDetails
      const cifPlanId = response.data.body?.cifPlanId
      return {
        accountHoldings,
        AccountYtdContributonList,
        navDetails,
        cifPlanId,
      }
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtTransactionDetails = (
  {
    accountNo,
    idType,
    idNo,
    cifPlanId,
    isGHRPHR,
  }: {
    accountNo: string
    idType: string
    idNo: string
    cifPlanId: string
    isGHRPHR: boolean | undefined
  },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: [
      'query-stmt-transaction-details',
      accountNo,
      idNo,
      cifPlanId,
      isGHRPHR,
    ],
    queryFn: async () => {
      const response = await StmtApi.queryTransactionDetails({
        source,
        stmtTransactionDetailsReqBodyPayload: {
          accountNo,
          idNo,
          idType,
          cifPlanId,
          isGHRPHR,
        },
      })
      const transactionLedger = response.data.body?.transactionLedger
      const isGHRPHRflag = response.data.body?.isGHRPHR
      return { transactionLedger, isGHRPHR: isGHRPHRflag }
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtRspDetails = (
  {
    accountNo,
    idNo,
    isGHRPHR,
  }: { accountNo: string; idNo: string; isGHRPHR: boolean | undefined },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-rsp-details', accountNo, idNo, isGHRPHR],
    queryFn: async () => {
      const response = await StmtApi.queryRspDetails({
        source,
        stmtRspDetailsReqBodyPayload: { accountNo, idNo, isGHRPHR },
      })
      return response.data.body?.rspDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtRwpDetails = (
  {
    accountNo,
    idNo,
    isGHRPHR,
  }: { accountNo: string; idNo: string; isGHRPHR: boolean | undefined },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-rwp-details', accountNo, idNo, isGHRPHR],
    queryFn: async () => {
      const response = await StmtApi.queryRwpDetails({
        source,
        stmtRwpDetailsReqBodyPayload: { accountNo, idNo, isGHRPHR },
      })
      return response.data.body?.rwpDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryStmtDistributorDetails = (
  {
    accountNo,
    idNo,
    isGHRPHR,
  }: { accountNo: string; idNo: string; isGHRPHR: boolean | undefined },
  { enabled }: { enabled: boolean }
) => {
  const { token, status } = selectAuthSessionState(store.getState()) ?? {}
  return useQuery({
    queryKey: ['query-stmt-distributor-details', accountNo, idNo, isGHRPHR],
    queryFn: async () => {
      const response = await StmtApi.queryDistributorDetails({
        source,
        stmtDistributorDetailsReqBodyPayload: { accountNo, idNo, isGHRPHR },
      })
      return response.data.body?.distributorDetails
    },
    enabled: !!token && status === StatusEnum.LoggedIn && enabled,
  })
}

export const useQueryRetirementCalculator = async (payload: {
  amount: number
  currentAge: number
  retiredAge: number
  annReturnsBeforeRetired: number
  annReturnsAfterRetired: number
  annualInflation: number
  annualLifeExpectancy: number
  monthly: boolean
}) => {
  const response = await StmtApi.retirementCalculator({
    stmtRetirementCalculatorReqBodyPayload: payload,
  })

  return response.data.body?.retirementCalculatorResponse
}
